const STORAGE_KEY = 'wifreo:recently-viewed:v2';
const LEGACY_KEYS = ['recently_viewed', 'viewedProducts'];
const MAX_ITEMS = 48;
const MAX_AGE = 90 * 24 * 60 * 60 * 1000;

function storageAvailable() {
  try {
    const testKey = `${STORAGE_KEY}:test`;
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (_error) {
    return false;
  }
}

function normalizeProduct(product) {
  if (!product || typeof product !== 'object') return null;

  const id = String(product.id || product.productId || '').trim();
  const handle = String(product.handle || '').trim();
  const title = String(product.title || product.productTitle || '').trim();
  const url = String(product.url || product.productUrl || (handle ? `/products/${handle}` : '')).trim();
  const image = String(product.image || product.imageUrl || product.productImg || product.featured_image || '').trim();

  if ((!id && !handle && !url) || !title || !url) return null;

  return {
    id: id || handle || url,
    handle,
    title,
    url,
    image,
    imageAlt: String(product.imageAlt || product.image_alt || title).trim(),
    imageWidth: Number(product.imageWidth || product.image_width) || 900,
    imageHeight: Number(product.imageHeight || product.image_height) || 900,
    viewedAt: Number(product.viewedAt) || Date.now(),
  };
}

function uniqueFreshProducts(products) {
  const cutoff = Date.now() - MAX_AGE;
  const seen = new Set();

  return products
    .map(normalizeProduct)
    .filter((product) => {
      if (!product || product.viewedAt < cutoff || seen.has(product.id)) return false;
      seen.add(product.id);
      return true;
    })
    .sort((a, b) => b.viewedAt - a.viewedAt)
    .slice(0, MAX_ITEMS);
}

function readJson(key) {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : [];
  } catch (_error) {
    return [];
  }
}

function migrateLegacyProducts() {
  const migrated = [];

  for (const key of LEGACY_KEYS) {
    const legacy = readJson(key);
    if (!Array.isArray(legacy)) continue;

    for (const [index, item] of legacy.entries()) {
      if (item && typeof item === 'object') {
        migrated.push({
          ...item,
          viewedAt: Number(item.viewedAt) || Date.now() - (legacy.length - 1 - index),
        });
      }
    }
  }

  return migrated;
}

export class RecentlyViewed {
  static getProducts() {
    if (!storageAvailable()) return [];

    const stored = readJson(STORAGE_KEY);
    const products = uniqueFreshProducts([
      ...(Array.isArray(stored) ? stored : []),
      ...migrateLegacyProducts(),
    ]);

    this.saveProducts(products);
    for (const key of LEGACY_KEYS) window.localStorage.removeItem(key);
    return products;
  }

  static saveProducts(products) {
    if (!storageAvailable()) return;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueFreshProducts(products)));
    } catch (_error) {
      // Browsing history is an enhancement; storage failures should never block shopping.
    }
  }

  static addProduct(product) {
    const normalized = normalizeProduct({ ...product, viewedAt: Date.now() });
    if (!normalized) return;

    const products = this.getProducts().filter((item) => item.id !== normalized.id);
    this.saveProducts([normalized, ...products]);
  }

  static clearProducts() {
    if (!storageAvailable()) return;
    window.localStorage.removeItem(STORAGE_KEY);
    for (const key of LEGACY_KEYS) window.localStorage.removeItem(key);
  }
}

function createResponsiveImage(product) {
  const image = document.createElement('img');
  image.className = 'recently-viewed__image';
  image.alt = product.imageAlt || product.title;
  image.loading = 'lazy';
  image.decoding = 'async';
  image.width = product.imageWidth;
  image.height = product.imageHeight;

  if (product.image) {
    image.src = product.image;
    try {
      const imageUrl = new URL(product.image, window.location.origin);
      const widths = [240, 360, 540, 720];
      image.srcset = widths
        .map((width) => {
          imageUrl.searchParams.set('width', width);
          return `${imageUrl.toString()} ${width}w`;
        })
        .join(', ');
      image.sizes = '(max-width: 749px) 42vw, (max-width: 989px) 33vw, 25vw';
    } catch (_error) {
      // Keep the original image URL when it cannot be safely transformed.
    }
  }

  return image;
}

function createCard(product) {
  const card = document.createElement('li');
  card.className = 'recently-viewed__item';

  const link = document.createElement('a');
  link.className = 'recently-viewed__card';
  link.href = product.url;
  link.setAttribute('aria-label', product.title);

  const media = document.createElement('span');
  media.className = 'recently-viewed__media';
  if (product.image) media.append(createResponsiveImage(product));

  const title = document.createElement('span');
  title.className = 'recently-viewed__title';
  title.textContent = product.title;

  link.append(media, title);
  card.append(link);
  return card;
}

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-recently-viewed-list]');
    this.emptyState = this.querySelector('[data-recently-viewed-empty]');
    this.clearButton = this.querySelector('[data-recently-viewed-clear]');
    this.sectionContent = this.querySelector('[data-recently-viewed-content]');
    this.isFull = this.dataset.mode === 'full';
    this.currentProductId = String(this.dataset.currentProductId || '');
    this.limit = Math.min(Number(this.dataset.limit) || (this.isFull ? MAX_ITEMS : 8), MAX_ITEMS);

    this.clearButton?.addEventListener('click', () => {
      RecentlyViewed.clearProducts();
      this.render([]);
    });

    this.render(this.products());
  }

  products() {
    return RecentlyViewed.getProducts()
      .filter((product) => product.id !== this.currentProductId)
      .slice(0, this.limit);
  }

  render(products) {
    if (!this.list || !this.sectionContent) return;

    this.list.replaceChildren(...products.map(createCard));
    this.list.hidden = products.length === 0;
    if (this.emptyState) this.emptyState.hidden = products.length > 0;

    if (!this.isFull) {
      this.hidden = products.length === 0;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
